import { useEffect, useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { TabsContent as RootTabsContent } from "@/components/ui/tabs";
import { getOverviewReport } from "@/api/reporting";

const COLORS = {
  gold: "#FBBF24",
  inHouse: "#22C55E",
  delivery: "#38BDF8",
  pickup: "#F59E0B",
  table: "#A78BFA",
  onlinePickup: "#EC4899",
  onlineDelivery: "#F97316",
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `£${amount.toLocaleString(undefined, {
    minimumFractionDigits: amount % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

const normalizeOrderValue = (rows = []) =>
  (rows || []).map((item) => ({
    label: item?.label || "",
    value: Number(item?.value || 0),
  }));

const normalizeOrderCountByServiceType = (rows = []) =>
  (rows || []).map((item) => ({
    label: item?.label || "",
    in_house: Number(item?.in_house || 0),
    delivery: Number(item?.delivery || 0),
    pickup: Number(item?.pickup || 0),
    table: Number(item?.table || 0),
    online_pickup: Number(item?.online_pickup || 0),
    online_delivery: Number(item?.online_delivery || 0),
  }));

const ValueTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-cblack2 border border-cblack6 px-3 py-2 rounded shadow-2xl">
        <p className="text-cwhite text-sm font-semibold">
          {formatCurrency(payload?.[0]?.value)}
        </p>
        <p className="text-cwhite3 text-[10px]">{label}</p>
      </div>
    );
  }

  return null;
};

const CountTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-cblack2 border border-cblack6 px-3 py-2 rounded shadow-2xl space-y-1">
        <p className="text-cwhite text-xs">{label}</p>
        {payload.map((entry) => (
          <p key={entry?.dataKey} className="text-[11px] text-cwhite3">
            <span className="text-cwhite">{entry?.name}:</span>{" "}
            {Number(entry?.value || 0)}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

const StatCardSkeleton = () => (
  <div className="bg-cblack4 border border-cblack6 rounded-lg p-4 flex-1 min-w-[250px]">
    <Skeleton className="h-4 w-28 mb-3 bg-cblack6/60" />
    <div className="flex items-end justify-between">
      <Skeleton className="h-8 w-20 bg-cblack6/60" />
      <Skeleton className="h-4 w-28 bg-cblack6/60" />
    </div>
  </div>
);

const ChartSkeleton = () => (
  <div className="h-80 w-full">
    <Skeleton className="h-full w-full bg-cblack6/60" />
  </div>
);

const StatCard = ({ label, value, change, vsLabel, isLoading }) => {
  if (isLoading) {
    return <StatCardSkeleton />;
  }

  const changeValue = Number(change || 0);
  const isNegative = changeValue < 0;

  return (
    <div className="bg-cblack4 border border-cblack6 rounded-lg p-4 flex-1 min-w-[250px]">
      <p className="text-cwhite3 text-sm font-medium mb-2">{label}</p>
      <div className="flex items-end justify-between">
        <p className="text-cwhite text-2xl font-bold">{value}</p>
        <div className="flex items-center gap-1">
          <TrendingUp
            size={16}
            className={isNegative ? "text-red-400" : "text-cgreen"}
          />
          <span
            className={
              isNegative
                ? "text-red-400 text-sm font-semibold"
                : "text-cgreen text-sm font-semibold"
            }
          >
            {changeValue}%
          </span>
          <span className="text-cwhite3 text-xs">
            {vsLabel || "vs last day"}
          </span>
        </div>
      </div>
    </div>
  );
};

function OverviewTab({ isActive }) {
  const [chartTab, setChartTab] = useState("order-value");
  const [period, setPeriod] = useState("yesterday");
  const [isLoading, setIsLoading] = useState(false);
  const [overviewData, setOverviewData] = useState(null);

  useEffect(() => {
    if (!isActive) return;

    let alive = true;

    const fetchOverview = async () => {
      setIsLoading(true);
      const response = await getOverviewReport({ period });

      if (!alive) return;

      if (response?.status === "success") {
        setOverviewData(response?.data || null);
      } else {
        setOverviewData(null);
        toast.error(response?.message || "Failed to load overview report.");
      }

      setIsLoading(false);
    };

    fetchOverview();

    return () => {
      alive = false;
    };
  }, [isActive, period]);

  const orderValueData = useMemo(
    () => normalizeOrderValue(overviewData?.chart?.order_value),
    [overviewData],
  );

  const orderCountData = useMemo(
    () =>
      normalizeOrderCountByServiceType(
        overviewData?.chart?.order_count_by_service_type,
      ),
    [overviewData],
  );

  const totalRevenueValue = formatCurrency(overviewData?.total_revenue?.value);
  const totalRevenueChange = Number(
    overviewData?.total_revenue?.change_percentage || 0,
  );
  const totalRevenueVsLabel =
    overviewData?.total_revenue?.vs_label || "vs last day";

  const totalOrdersValue = Number(
    overviewData?.total_orders?.value || 0,
  ).toLocaleString();
  const totalOrdersChange = Number(
    overviewData?.total_orders?.change_percentage || 0,
  );
  const totalOrdersVsLabel =
    overviewData?.total_orders?.vs_label || "vs last day";

  const PeriodSelect = () => (
    <Select value={period} onValueChange={setPeriod}>
      <SelectTrigger className="w-40 bg-cblack3 text-sm">
        <SelectValue placeholder="Select period" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="yesterday">Yesterday</SelectItem>
        <SelectItem value="last_7_days">Last 7 days</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <RootTabsContent value="overview" className="mt-6">
      <div className="pb-12">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-cwhite">Overview</h3>
          <PeriodSelect />
        </div>

        <div className="flex flex-wrap gap-4 mb-6">
          <StatCard
            label="Total Revenue"
            value={totalRevenueValue}
            change={totalRevenueChange}
            vsLabel={totalRevenueVsLabel}
            isLoading={isLoading}
          />
          <StatCard
            label="Total Orders"
            value={totalOrdersValue}
            change={totalOrdersChange}
            vsLabel={totalOrdersVsLabel}
            isLoading={isLoading}
          />
        </div>

        <div className="bg-cblack4 border border-cblack6 rounded-lg p-6">
          <Tabs
            valueKey="reporting-overview-chart"
            defaultValue="order-value"
            value={chartTab}
            onValueChange={setChartTab}
            className="w-full"
          >
            <div className="flex items-center justify-between">
              <TabsList className="bg-transparent">
                <TabsTrigger value="order-value">Order Value</TabsTrigger>
                <TabsTrigger value="order-count">
                  Order Count by Service Type
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="order-value" className="mt-6">
              {isLoading ? (
                <ChartSkeleton />
              ) : orderValueData?.length > 0 ? (
                <>
                  <div className="flex flex-wrap items-center gap-4 mb-4 select-none justify-center px-2">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-[#FBBF24]" />
                      <span className="text-xs font-medium text-cwhite3">Order Value (£)</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={orderValueData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="label"
                        stroke="#9CA3AF"
                        style={{ fontSize: "12px" }}
                        interval={0}
                        tick={{ fontSize: 12, fill: "#9CA3AF" }}
                      />
                      <YAxis
                        stroke="#9CA3AF"
                        style={{ fontSize: "12px" }}
                        formatter={(value) => `£${value}`}
                      />
                      <Tooltip content={<ValueTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={COLORS.gold}
                        strokeWidth={3}
                        dot={{ fill: COLORS.gold, r: 4 }}
                        activeDot={{ r: 6 }}
                      >
                        <LabelList
                          dataKey="value"
                          position="top"
                          offset={10}
                          fill={COLORS.gold}
                          fontSize={11}
                          formatter={(value) => formatCurrency(value)}
                        />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <div className="h-80 flex items-center justify-center">
                  <p className="text-cwhite3">
                    No data available for selected period
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="order-count" className="mt-6">
              {isLoading ? (
                <ChartSkeleton />
              ) : orderCountData?.length > 0 ? (
                <>
                  <div className="flex flex-wrap items-center gap-4 mb-4 select-none justify-center px-2">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-[#22C55E]" />
                      <span className="text-xs font-medium text-cwhite3">In House</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-[#38BDF8]" />
                      <span className="text-xs font-medium text-cwhite3">Delivery</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-[#F59E0B]" />
                      <span className="text-xs font-medium text-cwhite3">Pickup</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-[#A78BFA]" />
                      <span className="text-xs font-medium text-cwhite3">Table</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-[#EC4899]" />
                      <span className="text-xs font-medium text-cwhite3">Online Pickup</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-[#F97316]" />
                      <span className="text-xs font-medium text-cwhite3">Online Delivery</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={orderCountData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="label"
                        stroke="#9CA3AF"
                        style={{ fontSize: "12px" }}
                      />
                      <YAxis stroke="#9CA3AF" style={{ fontSize: "12px" }} />
                      <Tooltip content={<CountTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="in_house"
                        name="In House"
                        stroke={COLORS.inHouse}
                        strokeWidth={2.5}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="delivery"
                        name="Delivery"
                        stroke={COLORS.delivery}
                        strokeWidth={2.5}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="pickup"
                        name="Pickup"
                        stroke={COLORS.pickup}
                        strokeWidth={2.5}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="table"
                        name="Table"
                        stroke={COLORS.table}
                        strokeWidth={2.5}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="online_pickup"
                        name="Online Pickup"
                        stroke={COLORS.onlinePickup}
                        strokeWidth={2.5}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="online_delivery"
                        name="Online Delivery"
                        stroke={COLORS.onlineDelivery}
                        strokeWidth={2.5}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <div className="h-80 flex items-center justify-center">
                  <p className="text-cwhite3">
                    No data available for selected period
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </RootTabsContent>
  );
}

export default OverviewTab;
